import React, { useCallback, useEffect, useRef, useState } from "react";
import Pager from "../../components/Pager";
import Loader, { Spinner } from "../../components/Loader";
import { errorMessage } from "../../services";
import { answerSuggestion, getSuggestion, getSuggestions, sendSuggestion, sendSuggestionMessage } from "../../services/suggestions";
import { dateText, ROLE_LABELS } from "../../utils/format";

const CATEGORIES = [["feature", "💡 New feature"], ["design", "🎨 Design"], ["problem", "🐞 Something is wrong"], ["other", "💬 Other"]];
const STATUSES = [["new", "New"], ["planned", "Planned"], ["done", "Done"], ["declined", "Not planned"]];
const categoryLabel = (key) => (CATEGORIES.find(([k]) => k === key) || [key, key])[1];
const statusLabel = (key) => (STATUSES.find(([k]) => k === key) || [key, key])[1];
// How often new messages are fetched: the open chat, and the list
const CHAT_POLL_MS = 4000;
const LIST_POLL_MS = 15000;

// Sent when a chat is opened or answered, so the unread badge on the tab updates at once (AdminPanel.js)
export const SUGGESTIONS_SEEN_EVENT = "oz:suggestions-seen";
const announceSeen = () => window.dispatchEvent(new Event(SUGGESTIONS_SEEN_EVENT));

const timeText = (value) => new Date(value).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" });

// The whole conversation: the suggestion itself, an older single reply (before chat existed), then the messages
function chatOf(sg) {
  const lines = [{ from: "author", name: sg.authorName, text: sg.message, at: sg.createdAt, first: true }];
  if (sg.reply && !sg.messages.length) lines.push({ from: "ownezzy", name: "Ownezzy", text: sg.reply, at: sg.updatedAt });
  return lines.concat(sg.messages);
}

// One suggestion as a chat. New messages are fetched every few seconds while it is open.
function ChatPanel({ id, isSuper, notify, onBack, onChanged }) {
  const [sg, setSg] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState("");
  const end = useRef(null);
  const seenCount = useRef(-1);
  const count = sg ? sg.messages.length : 0;

  const load = useCallback(async () => {
    try {
      const { data } = await getSuggestion(id);
      setSg(data.suggestion);
      // Only when something new was read (first open or new messages), not on every check
      if (seenCount.current !== data.suggestion.messages.length) {
        seenCount.current = data.suggestion.messages.length;
        announceSeen();
      }
      setStatus((s) => s || data.suggestion.status);
    } catch (err) {
      notify(errorMessage(err), "error");
    }
  }, [id, notify]);

  useEffect(() => {
    setSg(null);
    setStatus("");
    load();
    const timer = setInterval(load, CHAT_POLL_MS);
    return () => clearInterval(timer);
  }, [load]);

  // Keep the newest message in view
  useEffect(() => { if (end.current) end.current.scrollIntoView({ block: "end" }); }, [count, id]);

  const send = async (e) => {
    e.preventDefault();
    const message = text.trim();
    if (!message) return;
    setSending(true);
    try {
      const { data } = await sendSuggestionMessage(id, message);
      setSg(data.suggestion);
      setText("");
      onChanged();
      announceSeen();
    } catch (err) {
      notify(errorMessage(err), "error");
    } finally {
      setSending(false);
    }
  };

  const changeStatus = async (next) => {
    setStatus(next);
    try {
      const { data } = await answerSuggestion(id, { status: next });
      setSg((s) => ({ ...s, status: data.suggestion.status }));
      notify(`Marked as ${statusLabel(next)}`);
      onChanged();
    } catch (err) {
      notify(errorMessage(err), "error");
    }
  };

  if (!sg) return <div className="chat-panel"><Loader label="Opening chat" /></div>;

  return (
    <div className="chat-panel">
      <div className="chat-head">
        <button type="button" className="chat-back" onClick={onBack} aria-label="Back to suggestions">←</button>
        <div className="chat-head-title">
          <b>{categoryLabel(sg.category)}</b>
          <small>{isSuper ? `${sg.authorName} · ${ROLE_LABELS[sg.authorRole] || sg.authorRole}` : `Sent ${dateText(sg.createdAt)}`}</small>
        </div>
        {isSuper ? (
          <select className="chat-status" value={status} onChange={(e) => changeStatus(e.target.value)} aria-label="Status">
            {STATUSES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        ) : (
          <span className={`suggestion-status s-${sg.status}`}>{statusLabel(sg.status)}</span>
        )}
      </div>
      <div className="chat-messages" aria-live="polite">
        {chatOf(sg).map((m, i) => {
          const mine = isSuper ? m.from === "ownezzy" : m.from === "author";
          return (
            <div key={`${m.at}-${i}`} className={`chat-bubble ${mine ? "is-mine" : "is-theirs"} ${m.first ? "is-first" : ""}`}>
              {!mine && <span className="chat-name">{m.from === "ownezzy" ? "Ownezzy" : m.name}</span>}
              <p>{m.text}</p>
              <time>{timeText(m.at)}</time>
            </div>
          );
        })}
        <div ref={end} />
      </div>
      <form className="chat-compose" onSubmit={send}>
        <textarea rows="1" value={text} maxLength={2000} placeholder={isSuper ? "Reply to this suggestion…" : "Write a message to Ownezzy…"}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) send(e); }} />
        <button className="primary-btn" disabled={sending || !text.trim()} aria-label="Send">{sending ? <Spinner /> : "Send"}</button>
      </form>
    </div>
  );
}

// Suggestions to Ownezzy, as chats. Store owners and partners start one and talk with Ownezzy there; the Super
// Admin sees everyone's, sets a status and replies. The history stays on each suggestion.
export default function SuggestionsTab({ user, notify }) {
  const isSuper = ["superadmin", "support"].includes(user.role); // answers for Ownezzy
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [meta, setMeta] = useState({ total: 0, page: 1, pages: 1 });
  const [status, setStatus] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ category: "feature", message: "" });
  const [sending, setSending] = useState(false);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      const { data } = await getSuggestions({ status, page: pageNumber, limit: 20 });
      setItems(data.suggestions);
      setCounts(data.counts || {});
      setMeta({ total: data.total, page: data.page, pages: data.pages });
      setError("");
    } catch (err) {
      if (!quiet) setError(errorMessage(err));
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [status, pageNumber]);

  useEffect(() => {
    load();
    const timer = setInterval(() => load(true), LIST_POLL_MS);
    return () => clearInterval(timer);
  }, [load]);

  const start = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await sendSuggestion(form);
      notify(data.message);
      setForm({ category: form.category, message: "" });
      setPageNumber(1);
      await load(true);
      setOpenId(data.suggestion._id);
    } catch (err) {
      notify(errorMessage(err), "error");
    } finally {
      setSending(false);
    }
  };

  const total = Object.values(counts).reduce((n, c) => n + c, 0);

  return (
    <>
      <div className="page-heading">
        <div>
          <p className="eyebrow">{isSuper ? "FROM CUSTOMERS AND PARTNERS" : "HELP US IMPROVE"}</p>
          <h1>Suggestions</h1>
          <p className="subheading">{isSuper ? "Chats with store owners and franchise partners about their ideas and problems." : "Tell us what would make Ownezzy better. Each suggestion is a chat with our team, and the history stays here."}</p>
        </div>
      </div>

      {!isSuper && (
        <form className="panel suggestion-form" onSubmit={start}>
          <div className="suggestion-cats" role="radiogroup" aria-label="Type of suggestion">
            {CATEGORIES.map(([key, label]) => (
              <button type="button" key={key} role="radio" aria-checked={form.category === key} className={form.category === key ? "active" : ""} onClick={() => setForm((f) => ({ ...f, category: key }))}>{label}</button>
            ))}
          </div>
          <label className="admin-field">New suggestion
            <textarea rows="3" required minLength={5} maxLength={2000} value={form.message} onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))} placeholder="e.g. I'd like to show a festival offer banner on my home page." />
          </label>
          <div className="suggestion-form-foot">
            <small className="muted">{form.message.length}/2000</small>
            <button className="primary-btn" disabled={sending || form.message.trim().length < 5}>{sending ? <><Spinner /> Sending</> : "Start chat"}</button>
          </div>
        </form>
      )}

      <div className="suggestion-filters">
        {[["", "All", total], ...STATUSES.map(([k, l]) => [k, l, counts[k] || 0])].map(([key, label, n]) => (
          <button key={key || "all"} className={status === key ? "active" : ""} onClick={() => { setStatus(key); setPageNumber(1); }}>{label} <b>{n}</b></button>
        ))}
      </div>

      {error && <div className="admin-error">{error}</div>}
      <div className={`chat-layout ${openId ? "has-open" : ""}`}>
        <div className="chat-list">
          {loading && !items.length && <Loader label="Loading suggestions" />}
          {!loading && !items.length && !error && <div className="panel admin-empty">{isSuper ? "No suggestions here yet." : "You haven't started any chats yet."}</div>}
          {items.map((item) => (
            <button type="button" key={item._id} className={`chat-item status-${item.status} ${openId === item._id ? "is-open" : ""}`} onClick={() => setOpenId(item._id)}>
              <span className="chat-item-top">
                <b>{isSuper ? item.authorName : categoryLabel(item.category)}</b>
                <time>{dateText(item.lastMessageAt || item.createdAt)}</time>
              </span>
              <span className="chat-item-text">{item.message}</span>
              <span className="chat-item-bottom">
                <small>{item.lastMessage ? `${item.lastMessage.from === "ownezzy" ? "Ownezzy" : isSuper ? item.authorName : "You"}: ${item.lastMessage.text}` : isSuper ? categoryLabel(item.category) : "Waiting for a reply"}</small>
                <span className={`suggestion-status s-${item.status}`}>{statusLabel(item.status)}</span>
                {item.unread > 0 && <em className="chat-unread">{item.unread}</em>}
              </span>
            </button>
          ))}
          {meta.pages > 1 && <Pager page={meta.page} pages={meta.pages} total={meta.total} noun="suggestions" onPage={setPageNumber} />}
        </div>
        {openId ? (
          <ChatPanel key={openId} id={openId} isSuper={isSuper} notify={notify} onBack={() => setOpenId(null)} onChanged={() => load(true)} />
        ) : (
          <div className="chat-panel chat-empty"><span aria-hidden="true">💬</span><p>Choose a suggestion to see the chat.</p></div>
        )}
      </div>
    </>
  );
}
